import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import "../styles/lineage-article.css";

const platformRows = [
  {
    component: "Storage foundation",
    azure: "OneLake, ADLS, Lakehouse, Warehouse",
    snowflake: "Snowflake tables, internal/external stages, Iceberg tables",
    databricks: "Delta Lake on cloud object storage",
    aws: "S3, Redshift, Iceberg, Glue Data Catalog",
  },
  {
    component: "Batch ingestion",
    azure: "Fabric Data Factory, Azure Data Factory, Dataflows Gen2",
    snowflake: "Stages, COPY INTO, Snowpipe, connectors",
    databricks: "Auto Loader, Lakeflow Connect, notebooks",
    aws: "Glue, DMS, S3 landing zones, Lambda",
  },
  {
    component: "Streaming / CDC",
    azure: "Eventstreams, mirroring, Event Hubs, ADF patterns",
    snowflake: "Snowpipe Streaming, Kafka connector, streams",
    databricks: "Structured Streaming, Auto Loader, Kafka/Kinesis connectors",
    aws: "Kinesis, MSK, DMS CDC, S3 events, Lambda",
  },
  {
    component: "Transformation",
    azure: "Spark notebooks, SQL, Dataflows, dbt jobs",
    snowflake: "SQL, dbt, Snowpark, dynamic tables",
    databricks: "Spark, SQL, Lakeflow pipelines, dbt",
    aws: "Glue Spark, EMR, Athena, Redshift SQL",
  },
  {
    component: "Data modelling",
    azure: "Lakehouse, Warehouse, Power BI semantic models",
    snowflake: "Schemas, views, dynamic tables, marts",
    databricks: "Delta tables, SQL views, marts, feature-ready tables",
    aws: "Glue Catalog, Athena views, Redshift marts",
  },
  {
    component: "Orchestration",
    azure: "Fabric pipelines, ADF triggers and activities",
    snowflake: "Tasks, streams, dynamic table refresh",
    databricks: "Lakeflow Jobs and scheduled workflows",
    aws: "Glue Workflows, Step Functions, EventBridge",
  },
  {
    component: "Quality checks",
    azure: "SQL checks, notebooks, Data Factory activities, semantic validation",
    snowflake: "SQL checks, dbt tests, tasks, dynamic table validation",
    databricks: "Expectations, notebooks, SQL checks, pipeline constraints",
    aws: "Glue checks, Athena queries, Deequ-style validation, custom tables",
  },
  {
    component: "Governance",
    azure: "Purview, Fabric governance, Power BI controls",
    snowflake: "Horizon Catalog, RBAC, masking, tags",
    databricks: "Unity Catalog, lineage, permissions",
    aws: "Lake Formation, Glue Catalog, IAM",
  },
  {
    component: "Security controls",
    azure: "Workspace roles, RLS, sensitivity labels, Purview policies",
    snowflake: "RBAC, masking policies, row access policies, secure views",
    databricks: "Unity Catalog grants, row/column controls, audit logs",
    aws: "IAM, Lake Formation permissions, KMS, row/column filters",
  },
  {
    component: "Consumption",
    azure: "Power BI, Fabric SQL endpoints, APIs",
    snowflake: "Power BI, Tableau, Looker, data sharing",
    databricks: "Databricks SQL, Power BI, ML features",
    aws: "QuickSight, Power BI, Athena, Redshift",
  },
];

const researchFindings = [
  {
    label: "Finding 1",
    title: "Every platform can implement the same architecture pattern.",
    body:
      "The tool names change, but the responsibilities remain: ingest reliably, preserve raw evidence, transform in layers, govern access, capture lineage and publish trusted data products.",
  },
  {
    label: "Finding 2",
    title: "The raw layer is the audit boundary.",
    body:
      "Teams lose recovery and reconciliation capability when they clean too early, overwrite source payloads or fail to store load metadata.",
  },
  {
    label: "Finding 3",
    title: "The semantic layer is an architecture decision.",
    body:
      "Power BI models, governed views and metric definitions shape how the business understands data. They need deliberate design so logic does not drift into individual reports.",
  },
];

const rawMetadataFields = [
  "source_system",
  "source_object",
  "extract_timestamp",
  "load_timestamp",
  "pipeline_run_id",
  "file_name",
  "record_hash",
  "raw_payload",
];

const controlPlaneItems = [
  {
    title: "Governance",
    body: "Ownership, classification, certification, retention, PII handling and production change control.",
  },
  {
    title: "Security",
    body: "Role-based access, row-level security, column masking, audit logging and environment separation.",
  },
  {
    title: "Orchestration",
    body: "Schedules, dependencies, retries, restartability, idempotency, backfills and alerting.",
  },
  {
    title: "Observability",
    body: "Freshness, row counts, failures, quality trends, cost signals and pipeline run evidence.",
  },
];

const trustPyramid = [
  ["Decisions, BI and AI", "The visible outputs: dashboards, analytics, APIs, copilots and operational decisions."],
  ["Semantic and data products", "Certified measures, reusable facts, governed dimensions and report-ready datasets."],
  ["Quality, lineage and governance", "The controls that explain, protect and validate data across the platform."],
  ["Raw evidence capture", "The foundation: source-faithful data with metadata, replayability and audit history."],
];

const architectureQuestions = [
  "What workloads are we supporting: BI, batch, streaming, AI/ML, operational data products or all of the above?",
  "How much data is batch, streaming or CDC?",
  "Where should transformations live: SQL, Spark, dbt, notebooks, semantic models or application code?",
  "Who owns semantic definitions?",
  "What governance, privacy and security controls are mandatory?",
  "What level of lineage and reconciliation is needed?",
  "What skills does the team actually have?",
  "How do we control cost and operational complexity?",
  "How will FinOps be managed: budgets, tagging, alerts, workload isolation, showback and cost anomaly detection?",
];

const platformDiagnostics = [
  {
    platform: "Azure / Fabric",
    managesWell: [
      "Power BI-native consumption and semantic modelling",
      "Integrated Microsoft estate: OneLake, Fabric, ADF, Purview, Entra ID",
      "Low-code and pro-code delivery across pipelines, notebooks, SQL and reports",
    ],
    shortcomings: [
      "Workspace and capacity sprawl can create governance and cost ambiguity",
      "Logic can drift between Dataflows, notebooks, SQL, semantic models and reports",
      "Fabric-era services may coexist awkwardly with legacy ADF/Synapse patterns",
    ],
    compensate:
      "Use explicit medallion boundaries, deployment pipelines, capacity governance, Purview-backed cataloguing and a semantic model standard that prevents every report from becoming its own data mart.",
  },
  {
    platform: "Snowflake",
    managesWell: [
      "SQL-first warehousing, elastic compute and governed sharing",
      "Clear role-based security primitives, masking policies and row access policies",
      "Warehouse-style marts, dynamic tables, streams and tasks",
    ],
    shortcomings: [
      "Tasks and streams can become hidden orchestration logic if not designed carefully",
      "The platform does not automatically solve semantic consistency across BI tools",
      "Cost discipline depends on warehouse sizing, query patterns and workload isolation",
    ],
    compensate:
      "Keep transformations in version-controlled SQL/dbt where possible, design task graphs deliberately, use resource monitors, separate raw/conformed/mart schemas and externalise metric definitions where multiple tools consume the same logic.",
  },
  {
    platform: "Databricks",
    managesWell: [
      "Large-scale Spark engineering, Delta Lake, streaming and ML workflows",
      "Lakehouse patterns with bronze/silver/gold tables and Unity Catalog governance",
      "Notebook-driven exploration that can graduate into jobs and declarative pipelines",
    ],
    shortcomings: [
      "Notebook sprawl can hide production logic and weaken reviewability",
      "Spark gives power, but also introduces cluster, shuffle and job-tuning complexity",
      "BI-friendly semantic consistency still needs deliberate modelling upstream",
    ],
    compensate:
      "Promote notebooks into tested packages or governed jobs, use Delta constraints and expectations, keep medallion contracts explicit, standardise cluster policies and publish gold marts that BI tools can consume without re-implementing business logic.",
  },
  {
    platform: "AWS",
    managesWell: [
      "Composable building blocks across S3, Glue, Athena, Redshift, Lambda and Step Functions",
      "Event-driven ingestion and flexible lake architecture",
      "Fine-grained cloud security through IAM, KMS, Lake Formation and network controls",
    ],
    shortcomings: [
      "The modular model can become service sprawl without a strong reference architecture",
      "Governance can fragment across IAM, Glue Catalog, Lake Formation and BI layers",
      "Teams must make more integration decisions themselves than in bundled platforms",
    ],
    compensate:
      "Define a reference architecture early: S3 zone conventions, Glue Catalog standards, Lake Formation permissions, orchestration ownership, cost tagging and a clear BI/semantic layer so the platform does not become a pile of well-managed services.",
  },
];

const engineeringLaws = [
  {
    title: "Functional data engineering",
    principle: "The raw layer should behave like an immutable append-only ledger.",
    implementation:
      "Downstream transformations should be deterministic: given the same raw input and the same transformation version, Silver and Gold outputs should be reproducible. If a Gold bug is found, the clean fix is to correct the transformation and replay from raw history, not patch the output table by hand.",
    example:
      "Use append-only raw tables, source metadata, batch IDs and transformation versioning so replay is safe across Fabric Lakehouse, Snowflake raw schemas, Databricks Delta bronze tables or S3 raw zones.",
  },
  {
    title: "Data contracts before schema drift",
    principle: "Do not merely survive schema drift; make schema change explicit.",
    implementation:
      "A data contract defines expected fields, types, nullability, keys, freshness and ownership at the producer-consumer boundary. It turns a surprise upstream change into a versioned agreement between software teams and data teams.",
    example:
      "Represent contracts in YAML or JSON schema, validate them during ingestion, and fail or quarantine incompatible payloads before they silently break reports.",
  },
  {
    title: "Data circuit breakers",
    principle: "Bad data should be architecturally prevented from reaching decision layers.",
    implementation:
      "Critical quality rules should halt, quarantine or route records into a dead-letter path when thresholds are breached. The point is not only to report failure; it is to prevent contamination of Silver, Gold and semantic layers.",
    example:
      "Stop a load if revenue drops by 50% without an approved business event, primary keys duplicate unexpectedly, or a required reference table fails freshness checks.",
  },
  {
    title: "Metrics as code",
    principle: "The semantic layer should not be trapped inside one workbook or dashboard.",
    implementation:
      "Shared metrics should be version-controlled, documented and reusable by dashboards, APIs, AI agents and operational applications. BI tools can render the metric, but should not be the only place where the metric is defined.",
    example:
      "Define metrics upstream through governed semantic models, dbt Semantic Layer, MetricFlow, Cube-style definitions or certified views so Power BI, APIs and analytical agents calculate the same measure.",
  },
];

const architectureLayers = [
  {
    title: "Sources",
    body: "Applications, SaaS tools, databases, files, streams and APIs.",
    design: "Classify each source by owner, volatility, latency, access pattern and business criticality.",
    evidence: "Source register, data contracts, extract schedule and ownership metadata.",
  },
  {
    title: "Ingestion",
    body: "Repeatable pipelines that capture metadata, watermarks and load evidence.",
    design: "Choose batch, CDC, event-driven or streaming patterns based on recovery and latency needs.",
    evidence: "Pipeline run IDs, watermarks, file names, row counts and rejected-record logs.",
  },
  {
    title: "Raw",
    body: "Source-faithful landing zone for replay, audit and recovery.",
    design: "Keep the raw layer close to the source shape and avoid destructive transformations.",
    evidence: "Immutable extracts, load timestamps, source payloads and retention rules.",
  },
  {
    title: "Validated",
    body: "Typed, deduplicated, standardised and quality-checked records.",
    design: "Apply schema, uniqueness, referential, freshness and accepted-value checks.",
    evidence: "Validation results, quarantine tables, issue counts and exception ownership.",
  },
  {
    title: "Curated",
    body: "Business-ready facts, dimensions, metrics and reusable data products.",
    design: "Model reusable business entities rather than one-off report extracts.",
    evidence: "Facts, dimensions, transformation tests, reconciliation totals and data product docs.",
  },
  {
    title: "Semantic",
    body: "Certified definitions, measures, relationships and access rules.",
    design: "Centralise reusable measures, relationships, row-level security and certified definitions.",
    evidence: "Measure catalogue, semantic model documentation, RLS rules and report dependencies.",
  },
  {
    title: "Consumption",
    body: "Dashboards, APIs, AI workflows, extracts and operational use cases.",
    design: "Design backwards from decisions, consumers, latency, trust requirements and access boundaries.",
    evidence: "Usage analytics, stakeholder sign-off, SLA expectations and decision traceability.",
  },
];

const medallionMap = [
  {
    layer: "Bronze",
    purpose: "Preserve evidence",
    fabric: "OneLake files or Lakehouse Delta tables",
    snowflake: "Raw schemas loaded from stages or Snowpipe",
    databricks: "Delta bronze tables from Auto Loader",
    aws: "Raw S3 prefixes catalogued by Glue",
  },
  {
    layer: "Silver",
    purpose: "Standardise and validate",
    fabric: "Spark or SQL-cleaned Lakehouse tables",
    snowflake: "Conformed schemas, streams/tasks, dbt models",
    databricks: "Cleaned Delta tables with expectations",
    aws: "Glue/EMR outputs, Athena-ready curated zones",
  },
  {
    layer: "Gold",
    purpose: "Serve the business",
    fabric: "Warehouse, semantic model and Power BI assets",
    snowflake: "Marts, secure views and metric-ready tables",
    databricks: "Gold marts and Databricks SQL models",
    aws: "Redshift marts, Athena views and BI datasets",
  },
];

const qualityChecks = [
  "Schema drift",
  "Row counts and volume changes",
  "Primary key uniqueness",
  "Nulls in required fields",
  "Accepted values",
  "Referential integrity",
  "Freshness and late-arriving data",
  "Source-to-target reconciliation",
  "Business rule validation",
  "Semantic model parity",
];

const platformCompass = [
  ["Azure / Fabric", "Power BI-centred analytics, OneLake, Data Factory and Purview"],
  ["Snowflake", "Governed SQL-first warehousing, sharing, dynamic tables and catalogues"],
  ["Databricks", "Lakehouse engineering, Spark, Delta, streaming, ML and Unity Catalog"],
  ["AWS", "Composable cloud-native storage, integration, compute and governance services"],
];

const platformProfiles = {
  azure: {
    name: "Azure / Fabric",
    summary:
      "Best when Power BI, Microsoft 365, Fabric, Azure integration and Purview-style governance are central to the operating model.",
    accent: "Microsoft-centred analytics",
    idealFor: ["Power BI estates", "Fabric lakehouses", "Microsoft governance", "Business reporting"],
  },
  snowflake: {
    name: "Snowflake",
    summary:
      "Best when SQL-first analytics, governed data sharing, warehousing, marts and strong separation of compute and storage are priorities.",
    accent: "Governed SQL platform",
    idealFor: ["Warehouse modernisation", "SQL teams", "Data sharing", "Business marts"],
  },
  databricks: {
    name: "Databricks",
    summary:
      "Best when Spark, Delta Lake, streaming, machine learning, notebooks and large-scale lakehouse engineering are central.",
    accent: "Engineering lakehouse",
    idealFor: ["Large-scale pipelines", "ML workflows", "Streaming", "Delta Lake"],
  },
  aws: {
    name: "AWS",
    summary:
      "Best when teams want modular cloud-native services across S3, Glue, Redshift, Athena, Lake Formation and event-driven patterns.",
    accent: "Composable cloud stack",
    idealFor: ["S3 data lakes", "Serverless integration", "Event-driven data", "Modular architecture"],
  },
};

const sources = [
  {
    label: "Microsoft Fabric Lakehouse",
    href: "https://learn.microsoft.com/en-us/fabric/data-engineering/lakehouse-overview",
  },
  {
    label: "Microsoft Fabric Data Factory",
    href: "https://learn.microsoft.com/en-us/fabric/data-factory/data-factory-overview",
  },
  {
    label: "Snowflake data loading",
    href: "https://docs.snowflake.com/en/user-guide/data-load-overview",
  },
  {
    label: "Databricks medallion architecture",
    href: "https://docs.databricks.com/aws/en/lakehouse/medallion",
  },
  {
    label: "Databricks ingestion",
    href: "https://docs.databricks.com/aws/en/ingestion/",
  },
  {
    label: "AWS Glue",
    href: "https://docs.aws.amazon.com/glue/latest/dg/what-is-glue.html",
  },
];

export default function PlatformAgnosticDataEngineeringArticle() {
  const [activeLayerIndex, setActiveLayerIndex] = useState(0);
  const [activePlatform, setActivePlatform] = useState("azure");

  const selectedLayer = architectureLayers[activeLayerIndex];
  const selectedPlatform = platformProfiles[activePlatform];

  const selectedPlatformRows = useMemo(
    () =>
      platformRows.map((row) => ({
        component: row.component,
        value: row[activePlatform],
      })),
    [activePlatform]
  );

  return (
    <div className="site-page lineage-page blog-article-page platform-article-page">
      <Seo
        title="Platform-Agnostic Data Engineering: Comparing Fabric, Snowflake, Databricks and AWS | Data Apparatus"
        description="A practical comparison of Azure, Snowflake, Databricks and AWS through the lens of platform-agnostic data architecture, ingestion, transformation, governance, lineage and analytics."
        canonicalPath="/blog/platform-agnostic-data-engineering"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Platform-Agnostic Data Engineering: Comparing Fabric, Snowflake, Databricks and AWS by Architecture",
          description:
            "A practical comparison of Azure, Snowflake, Databricks and AWS through the lens of data architecture.",
          author: {
            "@type": "Person",
            name: "Nurlan Alizada",
          },
          publisher: {
            "@type": "Organization",
            name: "Data Apparatus",
            url: "https://dataapparatus.com.au",
          },
          datePublished: "2026-06-30",
          mainEntityOfPage:
            "https://dataapparatus.com.au/blog/platform-agnostic-data-engineering",
        }}
      />

      <main className="blog-shell">
        <article className="blog-post">
          <header className="blog-header platform-hero">
            <Link to="/blog" className="blog-back-link">
              Blog
            </Link>
            <p className="blog-kicker">Architecture patterns across platforms</p>
            <h1>Platform-agnostic data engineering: comparing Fabric, Snowflake, Databricks and AWS by architecture.</h1>
            <p className="blog-subtitle">
              Azure, Snowflake, Databricks and AWS all provide powerful ways to build data
              platforms. The practical challenge is knowing which architectural
              responsibilities remain stable when the platform changes.
            </p>
            <div className="blog-meta">
              <span>Nurlan Alizada</span>
              <span>Data Apparatus</span>
              <span>June 30, 2026</span>
              <span>14 min read</span>
            </div>
          </header>

          <p className="blog-lead">
            Modern data engineering is often discussed through vendor names: Microsoft
            Fabric, Snowflake, Databricks, AWS, dbt, Delta Lake, Iceberg, Power BI and
            more. The names matter, but they are only useful after the architecture
            problem is clear.
          </p>

          <p>
            A platform is useful when it helps the team implement the right operating
            model: repeatable ingestion, preserved raw data, layered transformations,
            tested quality, visible lineage, governed access and reusable business
            definitions.
          </p>

          <section className="research-abstract">
            <span>Abstract</span>
            <p>
              This article compares Azure/Fabric, Snowflake, Databricks and AWS through a
              platform-agnostic architecture lens. The comparison is not intended to rank
              platforms universally. It evaluates how each ecosystem supports the same
              data engineering responsibilities: ingestion, raw evidence capture,
              transformation, modelling, orchestration, quality, governance, security,
              lineage and consumption.
            </p>
          </section>

          <div className="research-findings-grid">
            {researchFindings.map((finding) => (
              <section key={finding.label}>
                <span>{finding.label}</span>
                <h3>{finding.title}</h3>
                <p>{finding.body}</p>
              </section>
            ))}
          </div>

          <blockquote>
            Tools change faster than the data responsibilities they implement. A useful
            comparison should focus on ingestion, transformation, quality, governance,
            lineage and consumption rather than vendor branding.
          </blockquote>

          <figure className="platform-compass" aria-label="Platform-agnostic architecture compass">
            <div className="platform-compass-core">
              <span>Architecture lens</span>
              <strong>Shared data responsibilities</strong>
              <em>Ingestion, raw evidence, transformation, quality, lineage, governance and semantic trust.</em>
            </div>
            {platformCompass.map(([name, body]) => (
              <section key={name}>
                <h3>{name}</h3>
                <p>{body}</p>
              </section>
            ))}
          </figure>

          <h2>The common architecture pattern</h2>
          <p>
            Most modern data platforms follow the same operating model, even when each
            vendor gives it different names. Sources are ingested, raw data is landed,
            validated data is shaped, curated datasets become business products, semantic
            layers expose trusted definitions, and consumers use the result through BI,
            APIs, AI workflows or operational systems.
          </p>

          <figure className="platform-architecture-figure">
            <div className="architecture-flow" aria-label="Universal data architecture flow">
              {architectureLayers.map((layer, index) => (
                <button
                  type="button"
                  className={activeLayerIndex === index ? "active" : ""}
                  onClick={() => setActiveLayerIndex(index)}
                  key={layer.title}
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <h3>{layer.title}</h3>
                  <p>{layer.body}</p>
                </button>
              ))}
            </div>
            <aside className="architecture-detail-panel">
              <span>Selected layer</span>
              <h3>{selectedLayer.title}</h3>
              <p>{selectedLayer.design}</p>
              <strong>Evidence to capture</strong>
              <p>{selectedLayer.evidence}</p>
            </aside>
            <figcaption>
              Select a layer to see the architecture decision behind it. Governance,
              observability, security and lineage should run across every layer.
            </figcaption>
          </figure>

          <h2>Ingestion sets the architecture boundary</h2>
          <p>
            Ingestion affects far more than data movement. It determines whether the platform can
            replay a load, survive schema drift, explain late-arriving records, prove what
            arrived and support downstream reconciliation. A mature ingestion pattern
            captures source name, extract timestamp, pipeline run ID, file name, watermark,
            record hash and load status.
          </p>

          <p>
            In Azure and Fabric, this is commonly implemented with Data Factory pipelines,
            Azure Data Factory, Dataflows Gen2, notebooks, mirroring and OneLake
            shortcuts. In Snowflake, teams often use stages, COPY INTO, Snowpipe, Snowpipe
            Streaming or managed connectors. In Databricks, ingestion commonly uses Auto
            Loader, Structured Streaming, Lakeflow Connect and jobs. In AWS, the equivalent
            responsibilities are usually handled through S3, Glue, DMS, Kinesis, Lambda,
            Step Functions and EventBridge.
          </p>

          <div className="blog-callout">
            <h3>The architectural test</h3>
            <p>
              If a pipeline fails or a source system changes, can the team identify what
              arrived, what was rejected, what was transformed and how to replay the load
              without guessing?
            </p>
          </div>

          <h2>Raw layer: preserve the evidence</h2>
          <p>
            The raw layer is where many platforms become fragile. Teams clean too early,
            overwrite files, lose original payloads or fail to capture operational
            metadata. That creates problems when a migration needs validation, a dashboard
            does not reconcile, or an audit asks how a number was produced.
          </p>

          <div className="raw-evidence-panel">
            <div>
              <span>Raw evidence pattern</span>
              <h3>Landing data is not enough. Capture the operational evidence.</h3>
              <p>
                A source-faithful raw layer should support replay, backfill, lineage,
                reconciliation and incident investigation.
              </p>
            </div>
            <code>
              {rawMetadataFields.map((field) => (
                <span key={field}>{field}</span>
              ))}
            </code>
          </div>

          <h2>Bronze, silver and gold are responsibilities, not vendor features</h2>
          <p>
            Medallion architecture is often associated with lakehouse platforms, but the
            underlying idea is platform-agnostic. Bronze preserves source evidence. Silver
            standardises and validates. Gold expresses business-ready data products. You
            can implement that pattern in Fabric, Snowflake, Databricks or AWS if the layer
            boundaries are explicit.
          </p>

          <div className="medallion-comparison">
            {medallionMap.map((row) => (
              <section key={row.layer}>
                <div>
                  <span>{row.layer}</span>
                  <h3>{row.purpose}</h3>
                </div>
                <p><strong>Fabric:</strong> {row.fabric}</p>
                <p><strong>Snowflake:</strong> {row.snowflake}</p>
                <p><strong>Databricks:</strong> {row.databricks}</p>
                <p><strong>AWS:</strong> {row.aws}</p>
              </section>
            ))}
          </div>

          <h2>Transformation should create reusable data products</h2>
          <p>
            Transformation is where technical data becomes usable business data. This is
            where types are corrected, duplicates are resolved, keys are conformed, history
            is handled, business rules are applied and datasets become trusted enough for
            reporting or automation.
          </p>

          <p>
            The platform options vary. Fabric offers Spark notebooks, SQL, Dataflows and
            dbt jobs. Snowflake offers SQL, dbt, Snowpark, tasks and dynamic tables.
            Databricks offers Spark, SQL, Lakeflow pipelines and dbt. AWS offers Glue
            Spark jobs, EMR, Athena and Redshift. The engineering standard remains the
            same: modular logic, source control, testability, clear dependencies and
            documentation.
          </p>

          <h2>Platform comparison by engineering component</h2>
          <p>
            A useful platform comparison works better when it maps architectural
            components to the most natural services available in each ecosystem.
          </p>

          <p>
            The matrix below is one-to-one by responsibility. It shows the closest platform
            equivalents for the same engineering concern, so the comparison stays focused
            on architecture rather than product branding.
          </p>

          <div className="platform-workbench">
            <div className="platform-selector" aria-label="Select platform">
              {Object.entries(platformProfiles).map(([key, platform]) => (
                <button
                  type="button"
                  className={activePlatform === key ? "active" : ""}
                  onClick={() => setActivePlatform(key)}
                  key={key}
                >
                  <span>{platform.name}</span>
                  <small>{platform.accent}</small>
                </button>
              ))}
            </div>

            <section className="platform-profile-card">
              <span>Selected ecosystem</span>
              <h3>{selectedPlatform.name}</h3>
              <p>{selectedPlatform.summary}</p>
              <div className="platform-profile-tags">
                {selectedPlatform.idealFor.map((item) => (
                  <em key={item}>{item}</em>
                ))}
              </div>
            </section>

            <div className="platform-stack-list">
              {selectedPlatformRows.map((row) => (
                <section key={row.component}>
                  <span>{row.component}</span>
                  <p>{row.value}</p>
                </section>
              ))}
            </div>
          </div>

          <div className="research-matrix" role="table" aria-label="One-to-one platform comparison">
            <div className="research-matrix-row research-matrix-head" role="row">
              <span>Responsibility</span>
              <span>Azure / Fabric</span>
              <span>Snowflake</span>
              <span>Databricks</span>
              <span>AWS</span>
            </div>
            {platformRows.map((row) => (
              <div className="research-matrix-row" role="row" key={row.component}>
                <strong>{row.component}</strong>
                <span>{row.azure}</span>
                <span>{row.snowflake}</span>
                <span>{row.databricks}</span>
                <span>{row.aws}</span>
              </div>
            ))}
          </div>

          <h2>Where each platform is strong, and what needs design attention</h2>
          <p>
            A one-to-one tool comparison is useful, but it is not enough. Each platform has
            a natural centre of gravity. Good architecture uses that strength while
            deliberately addressing common design risks.
          </p>

          <div className="platform-diagnostics-grid">
            {platformDiagnostics.map((item) => (
              <section key={item.platform}>
                <h3>{item.platform}</h3>
                <div>
                  <strong>Manages well</strong>
                  <ul>
                    {item.managesWell.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <strong>Common shortcomings</strong>
                  <ul>
                    {item.shortcomings.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
                <p>
                  <strong>How to cater for this:</strong> {item.compensate}
                </p>
              </section>
            ))}
          </div>

          <h2>Four engineering patterns that travel across platforms</h2>
          <p>
            Beyond tool matching, there are several engineering patterns that remain
            useful across Fabric, Snowflake, Databricks and AWS.
          </p>

          <div className="engineering-laws">
            {engineeringLaws.map((law, index) => (
              <section key={law.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{law.title}</h3>
                <p className="law-principle">{law.principle}</p>
                <p>{law.implementation}</p>
                <code>{law.example}</code>
              </section>
            ))}
          </div>

          <h2>Data quality is not a final dashboard</h2>
          <p>
            Data quality works poorly when it is treated as a report that tells the team
            something went wrong after the business has already consumed the data. It is
            more useful when engineered into the platform as a set of controls, thresholds
            and evidence points that protect each layer of the architecture.
          </p>

          <p>
            A successful pipeline run only proves that code executed. It does not prove
            that the data is complete, fresh, unique, valid, reconciled or safe to publish.
            This is where data quality becomes architecture.
          </p>

          <p>
            At minimum, a production data platform should test for:
          </p>

          <div className="quality-grid">
            {qualityChecks.map((check) => (
              <span key={check}>{check}</span>
            ))}
          </div>

          <p>
            For example, a finance dataset should not move from raw to curated simply
            because the ingestion job completed. The platform should prove that source
            invoice counts match raw counts, rejected records are explainable, revenue
            totals reconcile to the finance source, and the Power BI measure still agrees
            with the certified gold dataset.
          </p>

          <p>
            The aim is not only to detect bad data, but to prevent it from silently
            reaching decision layers. This is where the circuit breaker pattern is useful:
            if a critical data contract is broken, the pipeline should halt,
            quarantine the affected records, or route them into an exception path before
            they contaminate Silver, Gold or semantic layers.
          </p>

          <div className="quality-equations">
            <span>Example evidence checks</span>
            <code>Source invoice count = Raw invoice count</code>
            <code>Raw invoice count &gt;= Silver valid invoice count</code>
            <code>Rejected records are quarantined with reasons</code>
            <code>Gold revenue total reconciles to finance source</code>
            <code>Power BI measure matches certified gold table</code>
            <code>Daily customer volume does not drop by 50% without an approved business event</code>
          </div>

          <p>
            The implementation differs by platform. In Fabric or Azure, these checks may
            run through notebooks, SQL, Data Factory activities, semantic model validation
            or Purview-connected metadata processes. In Snowflake, they may be implemented
            with SQL tests, dbt tests, tasks, streams, dynamic table validation or
            governance metadata.
          </p>

          <p>
            In Databricks, they may use expectations, Delta constraints, notebooks,
            Lakeflow pipelines or Unity Catalog metadata. In AWS, they may use Glue jobs,
            Athena validation queries, Deequ-style checks, Step Functions or custom
            validation tables. The implementation differs, but the responsibility is the
            same: a trusted data platform needs to produce evidence that its outputs are
            correct.
          </p>

          <h2>Lineage: use native metadata, then fill the gaps</h2>
          <p>
            Lineage deserves its own treatment, so I will keep it brief in this article. In
            the{" "}
            <Link to="/blog/living-data-lineage-map">
              previous Data Apparatus lineage article
            </Link>
            , I looked at the practical Microsoft delivery angle: tracing a number through
            Power BI semantic models, ADF pipelines, SQL transformations, bronze tables and
            source systems.
          </p>

          <p>
            For a platform-agnostic architecture, the key principle is simple: use
            out-of-the-box lineage wherever it exists, then extend it where native metadata
            stops. Purview and Fabric lineage, Unity Catalog, Snowflake governance
            metadata, Glue Catalog and Lake Formation all provide valuable signals. A
            holistic lineage strategy combines those native signals with code-level
            evidence, pipeline definitions, semantic-model dependencies, quality checks and
            business ownership.
          </p>

          <h2>Governance, security and orchestration are the control plane</h2>
          <p>
            A platform is not production-ready just because tables exist and dashboards
            refresh. Governance, security, orchestration and observability are the control
            plane that makes the platform operable. They define who can access data, how
            changes move to production, what happens when pipelines fail, and whether the
            team can explain the state of the estate.
          </p>

          <div className="control-plane-grid">
            {controlPlaneItems.map((item) => (
              <section key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </section>
            ))}
          </div>

          <h2>The semantic layer is part of data architecture</h2>
          <p>
            Power BI semantic models, dbt semantic definitions, governed views, metric
            stores and BI datasets are not just reporting concerns. They are where the
            organisation turns technical data into shared business language.
          </p>

          <p>
            If every dashboard defines revenue differently, the organisation does not have
            analytics. It has competing interpretations. Platform-agnostic architecture
            means separating business meaning from tool implementation. Metrics, access
            rules and analytical logic should be defined in governed, reusable layers so
            the same trusted definition can serve dashboards, APIs, AI workflows and
            operational systems.
          </p>

          <h2>Consumption: design backwards from decisions</h2>
          <p>
            A finance reconciliation report, operational dashboard, AI feature set and
            executive KPI pack do not need the same architecture. Consumption design should
            start with the decision, latency, grain, security boundary, reconciliation
            requirement and consumer workflow. The platform should then shape data
            products around those needs, rather than pushing source-system structures
            forward into dashboards.
          </p>

          <figure className="trust-pyramid">
            {trustPyramid.map(([title, body], index) => (
              <section key={title} style={{ "--level": index + 1 }}>
                <h3>{title}</h3>
                <p>{body}</p>
              </section>
            ))}
          </figure>

          <h2>Choose platforms by workload, operating model and economics</h2>
          <p>
            Platform selection works better when it starts with the workload, the team
            operating model, the governance requirement and the cost model. A platform that
            is excellent for one organisation can become expensive or fragile in another
            if the workload shape, team skills and controls do not match.
          </p>

          <p>
            Azure/Fabric is often attractive for Microsoft-centred organisations where
            Power BI, Microsoft 365, Purview, Azure integration and business-facing
            analytics matter. The architectural risk is capacity and workspace sprawl, so
            FinOps needs to include capacity monitoring, workspace governance, refresh
            discipline and ownership of semantic models.
          </p>

          <p>
            Snowflake is strong for SQL-first warehouse and lakehouse patterns, governed
            sharing and clean compute/storage separation. The architectural risk is
            uncontrolled warehouse usage, inefficient queries and poorly isolated
            workloads, so FinOps should include warehouse sizing, resource monitors, query
            optimisation, workload separation and cost attribution.
          </p>

          <p>
            Databricks is powerful where Spark, Delta Lake, streaming, ML workflows and
            large-scale engineering are central. The architectural risk is unnecessary
            cluster complexity, notebook sprawl and expensive always-on compute, so FinOps
            should include cluster policies, job cluster standards, autoscaling rules,
            Photon or serverless evaluation and clear promotion paths from notebooks to
            production jobs.
          </p>

          <p>
            AWS is strong when teams want modular cloud-native building blocks across
            storage, ingestion, orchestration, governance and analytics. The architectural
            risk is service sprawl and fragmented ownership, so FinOps should include
            tagging standards, S3 lifecycle policies, Athena and Redshift cost controls,
            Glue job monitoring, data transfer awareness and clear ownership of each
            service boundary.
          </p>

          <p>
            The most practical choice is usually the platform whose technical strengths,
            governance model, team capability and economic controls match the
            organisation's actual workload.
          </p>

          <div className="architecture-question-grid">
            {architectureQuestions.map((question) => (
              <span key={question}>{question}</span>
            ))}
          </div>

          <h2>What good platform-agnostic engineering looks like</h2>
          <p>
            A platform-agnostic data engineer does not ignore platform features. They
            understand them deeply enough to use each platform well, while still designing
            around stable architectural principles.
          </p>

          <ol className="blog-step-list">
            <li>Land raw data with metadata before transforming it.</li>
            <li>Separate technical cleanup from business modelling.</li>
            <li>Keep transformation logic modular, tested and version-controlled.</li>
            <li>Make lineage and ownership visible from source to dashboard.</li>
            <li>Build quality checks that produce evidence, not just status messages.</li>
            <li>Treat Power BI and semantic models as part of the architecture.</li>
            <li>Design for change in sources, definitions, teams and platforms.</li>
          </ol>

          <h2>Conclusion</h2>
          <p>
            The modern data stack will keep changing. Fabric will evolve. Snowflake will
            evolve. Databricks will evolve. AWS will evolve. New orchestration tools,
            catalogues, semantic layers and AI-assisted workflows will appear.
          </p>

          <p>
            The fundamentals will remain: preserve raw data, transform in clear layers,
            build reusable data products, validate data quality, capture lineage, govern
            access, document meaning and reconcile critical outputs.
          </p>

          <p>
            Platform-agnostic data engineering does not mean ignoring platform features.
            It means understanding each platform well while keeping the architecture clear
            enough to survive changes in tools, teams and business requirements.
          </p>

          <section className="blog-sources">
            <h2>Sources and further reading</h2>
            <ul>
              {sources.map((source) => (
                <li key={source.href}>
                  <a href={source.href} target="_blank" rel="noreferrer">
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <footer className="blog-post-footer">
            <p>
              Data Apparatus helps teams design platform-agnostic data architectures across
              ingestion, transformation, Power BI, governance, lineage and data quality.
            </p>
            <Link to="/contact" className="primary-btn">
              Discuss data architecture
            </Link>
          </footer>
        </article>
      </main>

      <Footer />
    </div>
  );
}
